import {
  createContext,
  Dispatch,
  useContext,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  AllTotals,
  Budget,
  ByItemType,
  HeaderOptions,
  ItemTypeListPerCalcOption,
  VersionTotals,
} from "../../types/budget/budget";
import { ActiveServiceAction, activeServiceReducer } from "./reducer-function";
import { ServicesService } from "../../services/services.service";

export type ServiceContextProps = {
  notification?: any;
  loadingService?: boolean;
  isLoadingCalcs: boolean;
  projectId?: string;
  activeService: Budget;
  activeCalcOption: HeaderOptions;
  temp: any;
  categoriesSnapshot: any[];
  setCategoriesSnapshot: (snapshot: any[]) => void;
  createEmptyService: (projectId: string) => void;
  updateService: (
    serviceId: string,
    serviceData: Budget,
    action?: string,
    version?: number,
    options?: { silent?: boolean }
  ) => Promise<any | null>;
  closeNotification: () => void;
  showNotification: (message: string, severity: string) => void;
  setIsLoadingCalcs: (value: boolean) => void;
  setLoadingService: (value: boolean) => void;
  activeServiceDispatch: Dispatch<ActiveServiceAction>;
  setActiveCalcOption: (option: HeaderOptions) => void;
  handleTempTaxData: (value: any, type: string) => void;
  cleanTempTaxData: () => void;
  getService: (projectId: string) => void;
};

const ServiceContext = createContext<ServiceContextProps>(
  {} as ServiceContextProps
);

function ServiceProvider({ children }: any) {
  const [activeService, activeServiceDispatch] = useReducer(
    activeServiceReducer,
    {} as Budget
  );

  const [activeCalcOption, setActiveCalcOption] = useState<HeaderOptions>("B");
  const [loadingService, setLoadingService] = useState(false);
  const [isLoadingCalcs, setIsLoadingCalcs] = useState(false);

  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "success",
  });
  const [categoriesSnapshot, setCategoriesSnapshot] = useState<any[]>([]);

  async function createEmptyService(projectId: string) {
    if (!!projectId)
      await createService({
        project: projectId,
        status: "OPEN",
        budgetServices: [],
        paymentSolicitation: [],
        itemTypeList: {} as ItemTypeListPerCalcOption,
        supplierList: [],
        addedItemTypeList: [],
        budgetValuesTotal: {
          byCalculationType: {} as AllTotals,
          byItemType: {} as ByItemType,
        },
        negociationValuesTotal: [] as VersionTotals[],
        closingValuesTotal: {} as VersionTotals,
        negotiationVersion: 0,
        files: [],
        versionHistory: [{ status: "OPEN", version: 0, comparisonVersion: 0 }],
        budgetIndex: 0,
        generalInfo: [
          {
            definition: "",
            rfp: false,
            tagGMP: "",
            local: "",
            city: "",
            country: "",
            state: "",
            address: "",
            checkIn: "",
            checkOut: "",
            meetingPeriod: "",
            availability: "",
            deadline: "",
            site: "",
            // version: 0,
          },
        ],
        baseValues: [],
        mainAppliedTaxes: [],
        budgetWideAppliedTaxes: [],
      });
  }

  async function updateService(
    serviceId: string,
    serviceData: Budget,
    _action?: string,
    _version?: number,
    options?: { silent?: boolean }
  ) {
    try {
      return await ServicesService.update(serviceId, serviceData);
    } catch (error) {
      if (!options?.silent) {
        showNotification(`Não foi possível atualizar o serviço`, "warning");
      }
      return null;
    }
  }

  function showNotification(message: string, severity = "success") {
    setNotification({ open: true, message, severity });
  }

  function closeNotification() {
    setNotification({ ...notification, open: false });
  }

  async function createService(data: Budget) {
    try {
      const serviceData = await ServicesService.create(data);
      activeServiceDispatch({
        payload: { ...serviceData, isApiUpdate: true },
        type: "NEW_ACTIVE_BUDGET",
      });
    } catch (error) {
      showNotification("Não foi possível criar", "warning");
    }
  }

  async function getService(projectId: string) {
    try {
      const serviceData = await ServicesService.getService(projectId);
      if (serviceData[0]) {
        activeServiceDispatch({
          payload: { ...serviceData[0], isApiUpdate: true },
          type: "NEW_ACTIVE_BUDGET",
        });
      } else {
        // Se não encontrou serviço existente, cria automaticamente
        await createEmptyService(projectId);
      }
    } catch (error) {
      showNotification("Não foi possível buscar o serviço", "warning");
    }
  }

  const temp = useRef<any>(null);

  const handleTempTaxData = (value: any, type: string) => {
    const taxTypeToAttributeMap: any = {
      SERVICE: "serviceTax",
      ISS: "iss",
      TARIFF: "tariff",
      LOCAL_TAX: "localTax",
      TOURISM_TAX: "tourismTax",
      FEE: "fee",
      IOF: "iof",
      MARKUP: "markup",
      OVER: "overhead",
      COMMISSION: "commission",
      INCENTIVE: "incentive",
      TRIBUTE: "tribute",
    };

    if (!temp.current) {
      temp.current = value;
    } else {
      const mapType = taxTypeToAttributeMap[type];
      const values = value?.budgetValues?.taxData?.[mapType];

      temp.current = {
        ...temp.current,
        budgetValues: {
          ...temp.current?.budgetValues,
          taxData: {
            ...temp.current?.budgetValues?.taxData,
            [mapType]: {
              ...temp.current?.budgetValues?.taxData?.[mapType],
              value: values.value,
              typeTax: values.typeTax,
            },
          },
        },
      };
    }
  };

  const cleanTempTaxData = () => (temp.current = null);

  return (
    <ServiceContext.Provider
      value={{
        isLoadingCalcs,
        notification,
        loadingService,
        activeService,
        activeCalcOption,
        temp: temp,
        categoriesSnapshot,
        setActiveCalcOption,
        createEmptyService,
        updateService,
        closeNotification,
        showNotification,
        setIsLoadingCalcs,
        activeServiceDispatch,
        setLoadingService,
        handleTempTaxData,
        cleanTempTaxData,
        getService,
        setCategoriesSnapshot,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
}

const useServices = () => useContext(ServiceContext);
export { ServiceProvider, useServices };
